import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Link from "@mui/material/Link";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { FeatureCard } from "../components/FeatureCard";
import { isFeatureIconName, type FeatureIconName } from "../components/icons/FeatureIcons";

import { TrialHighlight } from "../components/TrialHighlight";
import { PROVIDER_KINDS } from "../domain/labels";
import { SUPPORT_EMAIL } from "../domain/contact";
import { ENTITY, entityAddressLine, entityIdentifiersLine } from "../domain/entity";
import { VersionLine } from "../components/VersionLine";
import { normalizeIndianMobile } from "../api/login";

/**
 * ⚠️ Each entry states something that is RUNNING, except the one explicitly
 * marked as in development. Do not add a capability here before it is deployed
 * -- this is the public front door, and an aspiration written in the present
 * tense is just a false claim with good intentions.
 */
const AI_POINTS: readonly { icon: FeatureIconName; t: string; d: string }[] = [
  { icon: "composed", t: "Plans composed, not hand-built",
    d: "A decision graph assembles a plan and its invoice from the components a customer " +
       "actually selected, one step per component, instead of one rule per plan." },
  { icon: "tooling", t: "Open to your own tooling",
    d: "An MCP server exposes the platform to AI assistants, so you can ask questions of your " +
       "own data with the tools you already use." },
  { icon: "noprice", t: "No model sets a price",
    d: "Prices resolve from effective-dated catalog rows that were reviewed and committed. A " +
       "figure on an invoice traces back to a specific version — it is never generated." },
  { icon: "explain", t: "Explain any charge (in development)",
    d: "Answering \"why is this line this price?\" by walking that same provenance chain. Built " +
       "and tested; not yet switched on." },
];

const WHAT_IT_DOES: readonly { icon: FeatureIconName; t: string; d: string }[] = [
  { icon: "plans", t: "Plans and catalogue", d: "Define what you sell, and the RADIUS policy that enforces it." },
  { icon: "subscribers", t: "Subscribers", d: "Onboard, activate, renew, suspend — devices and mobile identities together." },
  { icon: "reports", t: "Reports", d: "Sessions, usage, recharges and expiry, scoped to your own network." },
  { icon: "billing", t: "Billing and GST", d: "Invoices that carry the right registered person, series and place of supply." },
];

/**
 * The public front door.
 *
 * 🔑 WHY THIS EXISTS AT ALL: until now the app had no unauthenticated surface —
 * RequireAuth redirected straight to ZITADEL, so there was nowhere to explain
 * what AnkhOS is or to let a new operator start. A visitor either had an
 * account or hit a login screen for a product they had never seen.
 *
 * ⛔ This page must NEVER read `useIdentity`, call the gateway, or render
 * anything tenant-specific. It is served to anyone on the internet. The only
 * auth it touches is starting a sign-in.
 */
export function Landing() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [signInMobile, setSignInMobile] = useState("");
  const [signInMobileError, setSignInMobileError] = useState(false);

  // Someone already signed in has no business on the front door.
  useEffect(() => {
    if (auth.isAuthenticated) navigate("/", { replace: true });
  }, [auth.isAuthenticated, navigate]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <Button size="small" component={RouterLink} to="/pricing" sx={{ mr: 1 }}>
            Pricing
          </Button>
          <Button size="small" onClick={() => void auth.signinRedirect()}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        {/* 🔑 The acronym is expanded ONCE, here in the eyebrow. The H1 carries
            the promise and the subhead says what the product does; neither
            repeats "operations suite". index.html's <title> and og: tags
            mirror this eyebrow + subhead — keep them in step. */}
        <Typography
          variant="overline"
          sx={{ fontWeight: 600, letterSpacing: 1.5, display: "block", mb: 1, color: "brand.link" }}
        >
          Operations Suite for Service Providers
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: 32, md: 44 } }}>
          The operating system for your service business — with an AI brain.
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4 }}>
          Plans, subscribers, usage and GST-compliant billing in one console — for network,
          internet, infrastructure, cloud, hosting, VPN, OTT and SaaS providers alike — including the
          many who are several of these at once. Every provider and reseller sees only its own
          accounts and the ones beneath it.
        </Typography>

        {/* 🔑 The primary CTA goes to PRICING, not to the form. Someone deciding
            whether to adopt AnkhOS wants the price and the free period before a
            registration form, and the trial is the thing that makes the price
            safe to look at. */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          {/* 🎨 The ONE amber element on this page. See theme/tokens.ts. */}
          <Button
            size="large"
            variant="contained"
            color="secondary"
            component={RouterLink}
            to="/pricing"
            endIcon={<ArrowForwardIcon />}
          >
            Get Started Free
          </Button>
          <Button size="large" variant="outlined" component={RouterLink} to="/register">
            Register as a Service Provider
          </Button>
          <Button size="large" onClick={() => void auth.signinRedirect()}>
            Sign in
          </Button>
        </Stack>

        {/* 🔑 The sign-in starts HERE, on our page, with the MOBILE NUMBER --
            the operator's identity (owner decision 2026-09-11). It goes to
            ZITADEL as login_hint in +91 form; ZITADEL 302s straight back to
            our /auth/login with it prefilled, so the visible flow is number
            here, SMS code on ankhos.io, then the console -- no ZITADEL page.
            Nothing is sent from this page: an OIDC auth request exists only
            after the redirect, so the code cannot be requested before it.
            See routes/SignIn.tsx. */}
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const typed = signInMobile.trim();
            const login_hint = typed ? normalizeIndianMobile(typed) : null;
            if (typed && !login_hint) {
              setSignInMobileError(true);
              return;
            }
            void auth.signinRedirect(login_hint ? { login_hint } : {});
          }}
          sx={{ mb: 2, maxWidth: 520 }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="Already registered? Your mobile number"
              value={signInMobile}
              onChange={(e) => {
                setSignInMobile(e.target.value);
                setSignInMobileError(false);
              }}
              error={signInMobileError}
              helperText={signInMobileError ? "Enter a 10-digit Indian mobile number." : " "}
              autoComplete="tel-national"
              fullWidth
              slotProps={{ htmlInput: { inputMode: "tel", "data-testid": "welcome-mobile" } }}
            />
            <Button type="submit" variant="outlined" sx={{ whiteSpace: "nowrap", alignSelf: "flex-start", height: 40 }}>
              Continue
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mb: 6, maxWidth: 620 }}>
          <TrialHighlight />
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
          {WHAT_IT_DOES.map((f) => (
            <Grid size={{ xs: 12, sm: 6 }} key={f.t}>
              <FeatureCard icon={f.icon} title={f.t} body={f.d} group="what" />
            </Grid>
          ))}
        </Grid>

        {/* 🚨 EVERY CLAIM HERE WAS CHECKED AGAINST WHAT IS RUNNING, because this
            is a public page and "AI-powered" is the easiest sentence in
            software to write untruthfully.

            Deployed, verified in-cluster:
              langgraph/langgraph-server  v0.2.0
              radiusapi/hotspot-mcp       v1.14.1

            NOT deployed: radagent. Its own README says "Nothing is deployed",
            and there is no Deployment for it in any namespace -- so the
            per-invoice explanation is described as coming, not offered. If it
            ships, change the tense here and nowhere else.

            🔑 The pricing sentence is the load-bearing one and it is
            architecturally true, not a slogan: prices resolve from
            effective-dated catalog rows via radbilling's ResolvePrice, and no
            model sits anywhere on that path. See domain/tiers.ts. */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            AI-assisted, and auditable by design
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
            An orchestration layer assembles plans and invoices from your catalog, component by
            component, and an MCP server exposes the platform to AI tooling. What it does not do
            is decide what you charge.
          </Typography>
          <Grid container spacing={3}>
            {AI_POINTS.map((f) => (
              <Grid size={{ xs: 12, sm: 6 }} key={f.t}>
                <FeatureCard icon={f.icon} title={f.t} body={f.d} group="ai" />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 🔑 Named rather than implied. "Service Provider" is accurate but
            abstract, and a hosting or OTT company reading only the abstract
            noun has no reason to think this is for them. The list below is the
            concrete half of the same claim.

            ⚠️ These are SEGMENTS, not the licence classes in OPERATOR_TYPES,
            and nothing here is collected -- see domain/labels.ts. */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Built for every kind of provider
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
            A plan is assembled from your namespace and your catalog, and neither carries
            anything about connectivity. Metering a VPN seat, a hosting package and a
            broadband line is the same operation over a different component — so one console
            covers all of them, with no per-industry build. The list below is examples, not a
            gate: if you bill subscribers on a recurring cycle across more than one plan and
            price list, this is built for you.
          </Typography>
          <Grid container spacing={2}>
            {PROVIDER_KINDS.map((k) => (
              <Grid size={{ xs: 12, md: 6 }} key={k.key}>
                <FeatureCard
                  icon={isFeatureIconName(k.key) ? k.key : "other"}
                  title={k.name}
                  body={k.blurb}
                  group="provider"
                  variant="band"
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 🔑 Said plainly on the public page rather than discovered after the
            form. Registration is reviewed, not instant — a provider identity
            carries the namespace every containment check on the platform
            trusts, so it is granted deliberately. */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Already registered?</strong> Sign in with the account you were given.{" "}
            <strong>New?</strong> Registration is reviewed before access is granted — you will be
            asked for your legal name, PAN, GST details if you are registered, and your licence
            details if you hold one.
          </Typography>
        </Box>
        <Divider sx={{ mt: 6, mb: 3 }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 3 }}
          sx={{ pb: 2 }}
        >
          <Link component={RouterLink} to="/pricing" variant="body2" color="text.secondary">
            Pricing
          </Link>
          <Link component={RouterLink} to="/legal/terms" variant="body2" color="text.secondary">
            Terms of Service
          </Link>
          <Link component={RouterLink} to="/legal/privacy" variant="body2" color="text.secondary">
            Privacy Policy
          </Link>
          <Link component={RouterLink} to="/legal/refunds" variant="body2" color="text.secondary">
            Cancellation &amp; Refunds
          </Link>
          <Link component={RouterLink} to="/contact" variant="body2" color="text.secondary">
            Contact us
          </Link>
          {/* The address beside the link, not only behind it. Someone who wants
              to email rather than fill a form should not have to load a page to
              find out how. */}
          <Link href={`mailto:${SUPPORT_EMAIL}`} variant="body2" color="text.secondary">
            {SUPPORT_EMAIL}
          </Link>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {ENTITY.brand} is a brand of {entityAddressLine()}, India. {entityIdentifiersLine()}.
        </Typography>
      <VersionLine />
      </Container>
    </Box>
  );
}
